import { ForumDensity, SidePanelState, Theme, ForumView } from "models/shared-types";
import { KEY, loadLocalStorageState, saveStateToLocalStorage } from "../utils/browser-storage";
import { derived, writable, type Readable, get } from "svelte/store";
import { getMediaQuery } from "$utils/mediaQuery";
import { getStores } from ".";
import { browser } from "$app/environment";
import type { UserStore } from "./user";

const USER_PREFERENCES_KEY = "user-preferences";
const THEME_KEY = "theme";
const LEFT_PANEL_KEY = "left-panel";
const FORUM_VIEW_KEY = "forum-view-size";

const isTabletAndDesktop = getMediaQuery("tablet");

export interface UserPreferencesState {
  [THEME_KEY]: Theme;
  [LEFT_PANEL_KEY]: SidePanelState;
  [FORUM_VIEW_KEY]: ForumView;
  [key: string]: any;
}

export const userPreferencesStore = (user: UserStore) => {
  let preferences: any = {};
  const $user = get(user);
  if ($user.isAuthed) {
    preferences = $user.preferences;
  }
  const initialState: UserPreferencesState = {
    theme:
      preferences[THEME_KEY] ||
      (browser && loadLocalStorageState(KEY)?.[USER_PREFERENCES_KEY]?.theme) ||
      (browser && window.matchMedia("(prefers-color-scheme: dark)").matches
        ? Theme.Dark
        : Theme.Light),
    [LEFT_PANEL_KEY]: !isTabletAndDesktop
      ? SidePanelState.Collapsed
      : preferences[LEFT_PANEL_KEY] || SidePanelState.Expanded,
    [FORUM_VIEW_KEY]: preferences[FORUM_VIEW_KEY] || ForumView.Default,
    ...preferences,
  };

  const { subscribe, update } = writable<UserPreferencesState>({
    ...initialState,
  });

  const updateAndSave = (
    fn: (state: UserPreferencesState) => UserPreferencesState,
    save = true
  ) => {
    update((state) => {
      const newState = fn(state);
      if ($user.isAuthed && save) {
        user.updatePreferences(newState);
        user.save();
      }
      return newState;
    });
  };
  return {
    subscribe,
    isDarkMode: (theme: Theme) => theme === Theme.Dark,
    setTheme: (theme: Theme) => {
      window.posthog?.identify({ theme });
      saveStateToLocalStorage(KEY, {
        [USER_PREFERENCES_KEY]: { theme },
      });
      updateAndSave((state) => ({ ...state, theme }));
    },
    toggleTheme: () => {
      updateAndSave((state) => {
        const newTheme = state[THEME_KEY] === Theme.Dark ? Theme.Light : Theme.Dark;
        saveStateToLocalStorage(KEY, {
          [USER_PREFERENCES_KEY]: { theme: newTheme },
        });
        return {
          ...state,
          [THEME_KEY]: newTheme,
        };
      });
    },
    setLeftPanelExpansion: (leftPanel: SidePanelState, save = true) => {
      saveStateToLocalStorage(KEY, {
        [USER_PREFERENCES_KEY]: { [LEFT_PANEL_KEY]: leftPanel },
      });
      updateAndSave((state) => ({ ...state, [LEFT_PANEL_KEY]: leftPanel }), save);
    },
    toggleLeftPanelExpansion: () => {
      updateAndSave((state) => {
        const collapseOrExpand =
          state[LEFT_PANEL_KEY] === SidePanelState.Expanded
            ? SidePanelState.Collapsed
            : SidePanelState.Expanded;
        saveStateToLocalStorage(KEY, {
          [USER_PREFERENCES_KEY]: { [LEFT_PANEL_KEY]: collapseOrExpand },
        });
        return {
          ...state,
          [LEFT_PANEL_KEY]: collapseOrExpand,
        };
      });
    },
    toggleForumViewSize: (isForumsFullWidthSet: boolean | undefined) => {
      updateAndSave((state) => {
        let forumViewSize;
        // if the user has not set the forums width yet,
        // when toggling the view size we set it to the opposite of the community setting one
        if (state[FORUM_VIEW_KEY] === ForumView.Default) {
          forumViewSize = isForumsFullWidthSet ? ForumView.Compact : ForumView.Full;
        } else {
          forumViewSize =
            state[FORUM_VIEW_KEY] === ForumView.Compact ? ForumView.Full : ForumView.Compact;
        }
        saveStateToLocalStorage(KEY, {
          [USER_PREFERENCES_KEY]: { [FORUM_VIEW_KEY]: forumViewSize },
        });
        return {
          ...state,
          [FORUM_VIEW_KEY]: forumViewSize,
        };
      });
    },
    setForumViewDensity: (key: string, viewDensity: ForumDensity) => {
      updateAndSave((state) => {
        saveStateToLocalStorage(KEY, {
          [USER_PREFERENCES_KEY]: { [key]: viewDensity },
        });
        return {
          ...state,
          [key]: viewDensity,
        };
      });
    },
  };
};
export type UserPreferencesStore = ReturnType<typeof userPreferencesStore>;

export const userPreferences: Readable<UserPreferencesState> = {
  subscribe(fn) {
    const store = getStores().userPreferences;
    return store.subscribe(fn);
  },
};

export const currentTheme = derived(userPreferences, ($userPreferences) => $userPreferences.theme);
export const isDarkTheme = derived(
  userPreferences,
  ($userPreferences) => $userPreferences[THEME_KEY] === Theme.Dark
);
export const isLightTheme = derived(
  userPreferences,
  ($userPreferences) => $userPreferences[THEME_KEY] === Theme.Light
);
export const isLeftPanelExpanded = derived(
  userPreferences,
  ($userPreferences) => $userPreferences[LEFT_PANEL_KEY] === SidePanelState.Expanded
);
export const isLeftPanelCollapsed = derived(
  userPreferences,
  ($userPreferences) => $userPreferences[LEFT_PANEL_KEY] === SidePanelState.Collapsed
);
export const isForumViewSizeFull = derived(
  userPreferences,
  ($userPreferences) => $userPreferences[FORUM_VIEW_KEY] === ForumView.Full
);
export const isForumViewSizeDefault = derived(
  userPreferences,
  ($userPreferences) => $userPreferences[FORUM_VIEW_KEY] === ForumView.Default
);
